import { useState, useContext, useEffect } from 'react'
import styled from 'styled-components'

import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
} from '@mui/material'

import tspImage from 'assets/images/tspdetails.png'
import { IntakeContext } from 'context/IntakeContext'
import theme from 'assets/theme'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { useFormDebounce } from 'hooks/useFormDebounce'

// Explicit fund order
const FUND_KEYS = [
  'tspGFund',
  'tspFFund',
  'tspCFund',
  'tspSFund',
  'tspIFund',
  'tspLFund',
]

const L_FUND_KEYS = [
  'tspLIncome',
  'tspL2025',
  'tspL2030',
  'tspL2035',
  'tspL2040',
  'tspL2045',
  'tspL2050',
  'tspL2055',
  'tspL2060',
  'tspL2065',
  'tspL2070',
  'tspL2075',
]

const StyledTileDiv = styled.div`
  text-align: center;
  position: relative;
  padding: 16px;
  height: 100%;
  cursor: pointer;
  border: 2px solid #f1f1f1;
  background: ${(props) => (props.active ? '#0671ad' : 'transparent')};
  color: ${(props) =>
    props.active ? '#fff !important' : '#344767 !important'};
  border-radius: 0.75rem;
  transition: 0.2s transform ease-in-out;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  &:hover {
    transition: 0.2s all ease-in-out;
    border: 2px solid #a3a7aa;
    transform: scale(1.02);
    background: ${(props) => (props.active ? '#0671ad' : '#f9f9f9')};
    color: ${(props) => (props.active ? '#fff' : '#344767')};
  }

  > h6,
  h5 {
    color: ${(props) => (props.active ? '#fff' : '#344767')};
    letter-spacing: 0.3px;
    font-weight: 500;
    line-height: 1.4;
    margin-top: 8px;
  }
`

export const TSPDetails = ({
  handleClose,
  open,
  initialAllocation,
  singleStep, // onboarding mode
  tspAllocation: controlledTspAllocation,
  setTspAllocation: setControlledTspAllocation,
  remoteManaged = false,
}) => {
  const [internalTspAllocation, setInternalTspAllocation] =
    useState(initialAllocation)

  const tspAllocation = remoteManaged
    ? controlledTspAllocation
    : internalTspAllocation
  const setTspAllocation = remoteManaged
    ? setControlledTspAllocation
    : setInternalTspAllocation

  const [step, setStep] = useState(0)
  const [tspTotalMixPercent, setTSPTotalMixPercent] = useState(0)

  const { updateIntakeStateAtKey } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit } = useFormDebounce()

  // recalc totals + sync intake state
  useEffect(() => {
    const parsedData = Object.fromEntries(
      Object.entries(tspAllocation).map(([key, data]) => [
        key,
        data.percentage || 0,
      ])
    )
    updateIntakeStateAndSubmit(
      () =>
        updateIntakeStateAtKey({ tspAllocation: JSON.stringify(parsedData) }),
      'finance'
    )()

    const total = Object.values(tspAllocation).reduce(
      (sum, d) => sum + (parseFloat(d.percentage) || 0),
      0
    )
    setTSPTotalMixPercent(total)

    // auto-skip intro if user already has allocations
    if (total > 0 && step === 0 && !singleStep) {
      setStep(1)
    }
  }, [tspAllocation])

  // build steps
  const steps = [
    {
      title: 'Enter Your TSP Fund Data',
      desc: (
        <>
          We recommend heading over to{' '}
          <a
            style={{ color: '#3498DB', textDecoration: 'underline' }}
            target="_blank"
            href="https://www.TSP.gov"
            rel="noreferrer"
          >
            www.TSP.gov
          </a>{' '}
          to sign in, then navigating to the{' '}
          <span style={{ fontWeight: 600, color: '#495057' }}>Investments</span>{' '}
          page to start copying over your fund information.
        </>
      ),
      action: (
        <img
          style={{
            alignSelf: 'center',
            objectFit: 'contain',
            width: '100%',
            height: '100%',
            marginTop: '24px',
            border: '1px solid #D2D6DA',
            borderRadius: 12,
          }}
          src={tspImage}
        />
      ),
    },
    {
      title: 'Which Funds Are You Invested In?',
      desc: <>Select all that apply:</>,
      action: (
        <Box mt={3}>
          <Grid container spacing={1} columns={6}>
            {FUND_KEYS.map((key) => {
              const data = tspAllocation[key]
              if (!data) return null
              return (
                <Grid item xs={2} key={key}>
                  <StyledTileDiv
                    active={data.selected}
                    onClick={() => {
                      setTspAllocation((prev) => {
                        const isLFundButton = data.label === 'L Funds'
                        if (isLFundButton && prev[key].selected) {
                          // deselect all L funds
                          return Object.fromEntries(
                            Object.entries(prev).map(([fk, fd]) => [
                              fk,
                              {
                                ...fd,
                                selected: fd.label.startsWith('L ')
                                  ? false
                                  : fd.selected,
                                percentage: fd.label.startsWith('L ')
                                  ? 0
                                  : fd.percentage,
                              },
                            ])
                          )
                        }
                        return {
                          ...prev,
                          [key]: {
                            ...prev[key],
                            selected: !prev[key].selected,
                            percentage: !prev[key].selected
                              ? prev[key].percentage
                              : 0,
                          },
                        }
                      })
                    }}
                  >
                    <Typography variant="h5">{data.label}</Typography>
                  </StyledTileDiv>
                </Grid>
              )
            })}
          </Grid>
        </Box>
      ),
    },
    {
      title: 'Which Specific L Funds Are You Invested In?',
      desc: <>Select all that apply:</>,
      action: (
        <Grid container mt={3} spacing={1} columns={4}>
          {L_FUND_KEYS.map((key) => {
            const data = tspAllocation[key]
            if (!data) return null
            return (
              <Grid item xs={2} key={key}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!data.selected}
                      onChange={(e) => {
                        const checked = e.target.checked
                        setTspAllocation((prev) => ({
                          ...prev,
                          [key]: {
                            ...prev[key],
                            selected: checked,
                            percentage: checked ? prev[key].percentage : 0,
                          },
                          tspLFund: {
                            ...prev.tspLFund,
                            selected:
                              checked ||
                              L_FUND_KEYS.some(
                                (lk) => lk !== key && prev[lk]?.selected
                              ),
                          },
                        }))
                      }}
                    />
                  }
                  label={data.label}
                />
              </Grid>
            )
          })}
        </Grid>
      ),
    },
    {
      title: 'TSP Fund Current Mix',
      desc: <>Fill in how much your current TSP mix is for each fund.</>,
      action: (
        <>
          {FUND_KEYS.concat(L_FUND_KEYS).map((key) => {
            const data = tspAllocation[key]
            if (!data) return null
            if (data.label === 'L Funds') return null
            if (!data.selected) return null
            return (
              <Grid item mt={2} mr={2} xs={12} sm={5.5} key={key}>
                {renderMuiFormField({
                  label: data.label,
                  inputType: 'percentage',
                  value: data.percentage,
                  onChange: (value) =>
                    setTspAllocation((prev) => ({
                      ...prev,
                      [key]: { ...prev[key], percentage: value },
                    })),
                })}
              </Grid>
            )
          })}
          <Box mt={4} display="flex" alignItems="center">
            <Typography variant="body1">TOTAL:</Typography>
            <Typography
              variant="h3"
              pl={2}
              color={tspTotalMixPercent !== 100 ? '#FF0000' : '#262627'}
            >
              {tspTotalMixPercent.toFixed(2)}%
            </Typography>
            <Typography ml={2} variant="body2">
              (Must be 100% to continue)
            </Typography>
          </Box>
        </>
      ),
    },
    {
      title: "Your total TSP Mix doesn't equal 100%",
      desc: (
        <>
          Try to get your mix percentages as accurate as possible. If you can’t
          get it to 100% we will display the{' '}
          <strong>{tspTotalMixPercent.toFixed(2)}%</strong> you entered for now.
        </>
      ),
      action: <></>,
    },
  ]

  // ====== SINGLE STEP MODE (used in onboarding) ======
  if (typeof singleStep === 'number') {
    const idx = singleStep - 1
    const stepData = steps[idx]
    if (!stepData) return null
    return (
      <div style={{ padding: 20 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          {stepData.title}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {stepData.desc}
        </Typography>
        <Grid container>{stepData.action}</Grid>
      </div>
    )
  }

  // ====== NORMAL DIALOG MODE (legacy use) ======
  const handleNext = () => {
    if (step === 1) {
      if (tspAllocation.tspLFund?.selected) {
        setStep(2)
      } else if (Object.values(tspAllocation).some((d) => d.selected)) {
        setStep(3)
      }
      return
    }
    if (
      step === 2 &&
      !Object.entries(tspAllocation).some(
        ([k, d]) => k.startsWith('tspL') && k !== 'tspLFund' && d.selected
      )
    ) {
      return
    }
    if (step === 3) {
      if (tspTotalMixPercent >= 1 && tspTotalMixPercent < 100) {
        setStep(4)
        return
      } else if (tspTotalMixPercent === 100) {
        handleClose?.()
        setStep(0)
        return
      } else if (tspTotalMixPercent > 100) {
        return
      }
    }
    if (step === 4) {
      handleClose?.()
      setStep(0)
      return
    }
    setStep((s) => s + 1)
  }

  const handleBack = () => {
    if (step === 0) {
      handleClose?.()
      return
    }
    if (step === 3) {
      if (tspAllocation.tspLFund?.selected) {
        setStep(2)
      } else {
        setStep(1)
      }
      return
    }
    setStep((s) => s - 1)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      sx={{
        '.MuiPaper-root': {
          padding: '20px 40px',
          borderRadius: '8px',
          [theme.breakpoints.up('sm')]: { padding: '30px 50px' },
          [theme.breakpoints.up('md')]: { padding: '40px 60px' },
        },
      }}
    >
      <DialogTitle variant="h4" sx={{ fontSize: '24px' }} color="#262627">
        {steps[step]?.title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2">{steps[step]?.desc}</Typography>
        <Grid container>{steps[step]?.action}</Grid>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="parcoRounded" color="outline" onClick={handleBack}>
          Back
        </Button>
        <Button variant="parcoRounded" onClick={handleNext}>
          {step === steps.length - 1 ? "I'll change it later" : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Static validator for onboarding
TSPDetails.isInvalid = ({ stepIndex, tspAllocation, tspTotalMixPercent }) => {
  if (stepIndex === 1) {
    return !Object.values(tspAllocation).some((d) => d.selected)
  }
  if (stepIndex === 2) {
    const hasAnyL = L_FUND_KEYS.some((k) => tspAllocation[k]?.selected)
    return !hasAnyL
  }

  if (stepIndex === 3) {
    return tspTotalMixPercent !== 100
  }
  return false
}
